import React, { useEffect, useMemo, useState } from 'react';
import { createManualRegistration } from '../../services/api';
import { calculateFeeBreakdown } from '../../utils/registrationFee';

const feeTypes = [
  { key: 'standard', label: 'Standard' },
  { key: 'student', label: 'Student' }
];

export default function ManualRegistrationModal({ paper, conference, onClose, onSuccess, show }) {
  const [authorEmail, setAuthorEmail] = useState('');
  const [feeType, setFeeType] = useState('standard');
  const [withAccompanied, setWithAccompanied] = useState(false);
  const [accompaniedPersonName, setAccompaniedPersonName] = useState('');
  const [paymentCurrency, setPaymentCurrency] = useState('EUR');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [amountEdited, setAmountEdited] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const authors = useMemo(() => {
    return Array.isArray(paper?.authors) ? paper.authors : [];
  }, [paper]);

  const fees = conference?.fees || {};
  const baseCurrency = fees.currency || 'EUR';
  const currencyOptions = useMemo(() => Array.from(new Set([baseCurrency, 'EUR', 'USD'])), [baseCurrency]);
  const { total: calculatedAmount, currency: displayCurrency } = calculateFeeBreakdown({
    feeType,
    fees,
    accompaniedPersonsCount: withAccompanied ? 1 : 0,
    paymentCurrency
  });

  useEffect(() => {
    if (!amountEdited) {
      setPaymentAmount(String(calculatedAmount));
    }
  }, [calculatedAmount, amountEdited]);

  useEffect(() => {
    if (!withAccompanied) setAccompaniedPersonName('');
  }, [withAccompanied]);

  useEffect(() => {
    if (!paper) return;
    // Find corresponding author, fallback to first author
    const correspondingAuthor = authors.find(a => a.isCorresponding);
    const defaultEmail = correspondingAuthor?.email || authors[0]?.email || '';
    setAuthorEmail(defaultEmail);
    setFeeType('standard');
    setWithAccompanied(false);
    setPaymentCurrency(baseCurrency);
    setPaymentAmount('');
    setAmountEdited(false);
    setNotes('');
    setAccompaniedPersonName('');
    setError('');
    setSuccessMessage('');
  }, [paper, authors, baseCurrency]);

  const handleSubmit = async () => {
    if (!paper || !conference) return;
    if (!authorEmail) {
      setError('Please select an author email.');
      return;
    }

    if (withAccompanied && !accompaniedPersonName.trim()) {
      setError('Please enter the accompanied person name.');
      return;
    }

    const parsedAmount = Number(paymentAmount);
    if (!Number.isFinite(parsedAmount) || parsedAmount < 0) {
      setError('Please enter a valid total amount.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await createManualRegistration({
        conferenceId: conference._id,
        paperId: paper._id,
        authorEmail,
        feeType,
        accompaniedPersonsCount: withAccompanied ? 1 : 0,
        accompaniedPersonName: withAccompanied ? accompaniedPersonName.trim() : undefined,
        paymentCurrency,
        paymentAmount: parsedAmount,
        notes: notes || undefined
      });
      setSuccessMessage('Manual registration completed.');
      if (onSuccess) onSuccess();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to create manual registration.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!show || !paper || !conference) return null;

  return (
    <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Manual Registration</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <p><strong>Paper:</strong> {paper.manuscriptTitle}</p>
            <p><strong>Conference:</strong> {conference.title}</p>

            <div className="mb-3">
              <label className="form-label">Author Email</label>
              <select
                className="form-select"
                value={authorEmail}
                onChange={(e) => setAuthorEmail(e.target.value)}
                disabled={authors.length === 0}
              >
                {authors.length === 0 && (
                  <option value="">No authors available</option>
                )}
                {authors.map((author, index) => (
                  <option key={author._id || `${author.email || 'author'}-${index}`} value={author.email || ''}>
                    {author.firstName} {author.lastName} ({author.email || 'no email'})
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Payment Currency</label>
              <select className="form-select" value={paymentCurrency} onChange={(e) => setPaymentCurrency(e.target.value)} disabled={loading}>
                {currencyOptions.map((cur) => (
                  <option key={cur} value={cur}>{cur}</option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Select Fee Type</label>
              <select className="form-select" value={feeType} onChange={(e) => setFeeType(e.target.value)} disabled={loading}>
                {feeTypes.map((f) => {
                  const { base } = calculateFeeBreakdown({ feeType: f.key, fees, accompaniedPersonsCount: 0, paymentCurrency });
                  return (
                    <option key={f.key} value={f.key}>{f.label} ({displayCurrency} {base})</option>
                  );
                })}
              </select>
            </div>

            <div className="mb-3">
              <div className="form-check mb-2">
                <input
                  type="checkbox"
                  id="manualWithAccompanied"
                  className="form-check-input"
                  checked={withAccompanied}
                  onChange={(e) => setWithAccompanied(e.target.checked)}
                  disabled={loading}
                />
                <label htmlFor="manualWithAccompanied" className="form-check-label">
                  Include Accompanied Person {`(+${displayCurrency} ${calculateFeeBreakdown({ feeType, fees, accompaniedPersonsCount: 1, paymentCurrency }).accompaniedFee})`}
                </label>
              </div>
              {withAccompanied && (
                <div className="mt-2">
                  <label className="form-label">Accompanied Person Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={accompaniedPersonName}
                    onChange={(e) => setAccompaniedPersonName(e.target.value)}
                    placeholder="Full name"
                    disabled={loading}
                  />
                </div>
              )}
              <label className="form-label">Total Amount</label>
              <div className="input-group">
                <span className="input-group-text">{displayCurrency}</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-control"
                  value={paymentAmount}
                  onChange={(e) => {
                    setAmountEdited(true);
                    setPaymentAmount(e.target.value);
                  }}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setAmountEdited(false);
                    setPaymentAmount(String(calculatedAmount));
                  }}
                  disabled={loading}
                >
                  Reset
                </button>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Comments</label>
              <textarea
                className="form-control"
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any internal notes for this manual registration..."
              />
            </div>

            {error && (
              <div className="alert alert-danger py-2">{error}</div>
            )}
            {successMessage && (
              <div className="alert alert-success py-2">{successMessage}</div>
            )}
            {loading && <div className="small text-muted mb-2">Processing...</div>}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Close</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={loading || !authorEmail}>Register</button>
          </div>
        </div>
      </div>
    </div>
  );
}
